import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable, of, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthSessionService } from '../services/auth-session.service';

/**
 * Auth guard con validazione scadenza token.
 * Token valido → passa.
 * Token scaduto → silent refresh → popup MSAL (via AuthSessionService) → /login solo se fallisce tutto.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private static readonly LOG_PREFIX = '[AmSuite Auth]';

  private msalService = inject(MsalService);
  private router = inject(Router);
  private authSessionService = inject(AuthSessionService);

  canActivate(): Observable<boolean | UrlTree> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      console.log(`${AuthGuard.LOG_PREFIX} AuthGuard — no MSAL accounts, redirect /login`);
      return of(this.router.createUrlTree(['/login']));
    }

    const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];
    const exp = activeAccount?.idTokenClaims?.exp;

    if (exp && exp * 1000 > Date.now()) {
      return of(true);
    }

    console.log(`${AuthGuard.LOG_PREFIX} AuthGuard — id token expired, attempting recovery`, {
      account: activeAccount?.username,
      exp,
    });

    return from(this.authSessionService.attemptSilentRefresh()).pipe(
      switchMap(token => {
        if (token) {
          console.log(`${AuthGuard.LOG_PREFIX} AuthGuard — silent refresh OK`);
          return of(true);
        }
        return from(this.authSessionService.reauthenticate('AuthGuard: id token expired')).pipe(
          map(success => {
            if (success) {
              console.log(`${AuthGuard.LOG_PREFIX} AuthGuard — reauthenticate OK`);
              return true;
            }
            console.warn(`${AuthGuard.LOG_PREFIX} AuthGuard — reauthenticate failed, redirect /login`);
            return this.router.createUrlTree(['/login']);
          })
        );
      })
    );
  }
}
